import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';

export interface FundraisingItem {
  id: string;
  title: string;
  description: string;
  targetAmount: number;
  currentAmount: number;
  currencySymbol?: string;
  targetLabel?: string;
}

interface TotalCollectedResponse {
  total_collected: number;
}

@customElement('rftw-transparency')
export class RftwTransparency extends LitElement {
  /** Section small header tag */
  @property({ type: String }) sectionTag = 'TRANSPARENCIA TOTAL';

  /** Main section title */
  @property({ type: String }) title = 'A dónde va cada colón';

  /** Section description text */
  @property({ type: String }) description =
    'Este es el equipo médico urgente que IAR Nosara necesita hoy. Sigue en vivo cuánto falta para cada equipo médico, cada jersey y cada donación mueve la barra.';

  /** List of medical equipment fundraising goals */
  @property({ type: Array }) items: FundraisingItem[] = [
    {
      id: 'icu-unit',
      title: 'Unidades de cuidados intensivos',
      description:
        'Jaulas especializadas para mantener y monitorear animales en estado crítico o en recuperación.',
      targetAmount: 2100,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$2,100',
    },
    {
      id: 'oxygen-generator',
      title: 'Generador de oxígeno',
      description:
        'Proporciona oxígeno concentrado a animales con dificultades respiratorias o en estado crítico.',
      targetAmount: 1500,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$1,500',
    },
    {
      id: 'fluid-warmer',
      title: 'Calentador de fluidos',
      description:
        'Calienta los líquidos administrados para ayudar a mantener estable la temperatura corporal del animal.',
      targetAmount: 600,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$600',
    },
    {
      id: 'cavitron',
      title: 'Cavitron',
      description:
        'Equipo ultrasónico utilizado para limpiezas dentales y eliminación de sarro.',
      targetAmount: 500,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$500',
    },
    {
      id: 'centrifuge',
      title: 'Centrífuga',
      description:
        'Separa los componentes de muestras de sangre u otros fluidos para realizar análisis y diagnósticos.',
      targetAmount: 400,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$400',
    },
    {
      id: 'laryngoscope-set',
      title: 'Set de laringoscopio',
      description:
        'Permite visualizar las vías respiratorias y facilita la intubación durante anestesia o emergencias.',
      targetAmount: 350,
      currentAmount: 0,
      currencySymbol: '$',
      targetLabel: '$400',
    },
  ];

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadCollectedTotal();
  }

  static styles = css`
    :host {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    .transparency-container {
      background-color: #eaf1ea;
      padding: 4.5rem 2rem;
      box-sizing: border-box;
    }

    .content-wrapper {
      max-width: 1200px;
      margin: 0 auto;
    }

    .section-tag {
      font-size: 0.75rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--wa-color-orange-50, #d95328);
      margin-bottom: 0.5rem;
    }

    .section-title {
      font-size: clamp(2rem, 4vw, 2.75rem);
      font-weight: 800;
      color: #1b2e23;
      margin: 0 0 1rem 0;
      letter-spacing: -0.02em;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .description {
      font-size: 0.98rem;
      line-height: 1.6;
      color: var(--wa-color-text-normal, #4a5568);
      margin: 0 0 2.25rem 0;
      max-width: 680px;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .featured-goal {
      background-color: #ffffff;
      border-radius: 12px;
      padding: 2rem;
      box-shadow: 0 8px 24px rgba(27, 46, 35, 0.08);
      border-top: 4px solid #ef6c31;
    }

    .featured-goal .item-title {
      font-size: clamp(1.4rem, 3vw, 2rem);
    }

    .featured-goal .item-amount {
      font-size: clamp(1.4rem, 3vw, 2rem);
    }

    .featured-goal .item-desc {
      max-width: 680px;
      margin-bottom: 1.75rem;
    }

    .featured-goal .progress-bar-bg {
      height: 12px;
    }

    .featured-goal .status-text {
      font-size: 0.9rem;
    }

    .primary-action {
      margin-top: 1.25rem;
      border: 0;
      border-radius: 6px;
      padding: 0.75rem 1.25rem;
      background-color: #ef5525;
      color: #ffffff;
      font: 700 0.9rem var(--font-family, 'Open Sans', sans-serif);
      cursor: pointer;
    }

    .primary-action:hover {
      background-color: #d9481d;
    }

    .secondary-goals {
      margin-top: 2.5rem;
    }

    .secondary-title {
      color: #1b2e23;
      font: 800 1.15rem var(--font-family, 'Open Sans', sans-serif);
      margin: 0 0 0.75rem;
    }

    .secondary-list {
      background: rgba(255, 255, 255, 0.68);
      border-top: 1px solid #d5dfd5;
      border-bottom: 1px solid #d5dfd5;
    }

    .secondary-goal {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 1.25rem;
      align-items: center;
      padding: 1rem 0.75rem;
      border-bottom: 1px solid #d5dfd5;
    }

    .secondary-goal:last-child {
      border-bottom: 0;
    }

    .secondary-goal .item-title {
      font-size: 1rem;
    }

    .secondary-goal .item-desc {
      margin: 0.25rem 0 0;
      font-size: 0.82rem;
    }

    .secondary-goal .item-amount {
      font-size: 1rem;
    }

    .card {
      background-color: #ffffff;
      border-radius: 12px;
      padding: 1.75rem 1.75rem 1.5rem;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.02);
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      margin-bottom: 0.25rem;
    }

    .item-title {
      font-size: 1.15rem;
      font-weight: 700;
      color: #1b2e23;
      margin: 0;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .item-amount {
      font-size: 1.15rem;
      font-weight: 700;
      color: #1b2e23;
      white-space: nowrap;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    .item-desc {
      font-size: 0.875rem;
      color: #8c9790;
      margin: 0 0 1.5rem 0;
      line-height: 1.4;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    /* Progress Bar Styles */
    .progress-bar-bg {
      height: 8px;
      background-color: #dfe7df;
      border-radius: 4px;
      overflow: hidden;
      width: 100%;
    }

    .progress-bar-fill {
      height: 100%;
      background: linear-gradient(90deg, #f2a32a 0%, #ef6c31 52%, #d84b2a 100%);
      border-radius: 4px;
      box-shadow: 0 0 10px rgba(216, 75, 42, 0.45);
      transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .status-text {
      margin-top: 0.75rem;
      font-size: 0.78rem;
      color: #8c9790;
      font-weight: 500;
      font-family: var(--font-family, 'Open Sans', sans-serif);
    }

    @media (max-width: 600px) {
      .transparency-container {
        padding: 3rem 1.25rem;
      }

      .description {
        margin-bottom: 2rem;
      }

      .featured-goal {
        padding: 1.25rem;
      }

      .featured-goal .card-header {
        flex-direction: column;
        gap: 0.375rem;
      }

      .featured-goal .item-amount {
        font-size: 1rem;
      }

      .secondary-goal {
        gap: 0.75rem;
      }

      .secondary-goal .item-desc {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
      }
    }
  `;

  private _calculatePercentage(current: number, target: number): number {
    if (!target || target <= 0) return 0;
    return Math.min(100, Math.round((current / target) * 100));
  }

  private _calculateRemaining(current: number, target: number): number {
    return Math.max(0, target - current);
  }

  private _formatCurrency(amount: number, symbol = '$'): string {
    return `${symbol}${amount.toLocaleString('en-US')}`;
  }

  private async _loadCollectedTotal(): Promise<void> {
    try {
      const response = await fetch('/api/v1/orders/total-collected');
      if (!response.ok) return;

      const { total_collected: totalCollected } =
        (await response.json()) as TotalCollectedResponse;
      if (!Number.isFinite(totalCollected) || totalCollected < 0) return;

      let remainingTotal = totalCollected;
      this.items = this.items.map((item) => {
        const currentAmount = Math.min(remainingTotal, item.targetAmount);
        remainingTotal = Math.max(0, remainingTotal - currentAmount);

        return { ...item, currentAmount };
      });
    } catch {
      // Keep the initial zero-progress state when the public total is unavailable.
    }
  }

  render() {
    const [primaryGoal, ...secondaryGoals] = this.items;

    if (!primaryGoal) {
      return html``;
    }

    const renderGoalProgress = (item: FundraisingItem) => {
      const percentage = this._calculatePercentage(
        item.currentAmount,
        item.targetAmount
      );
      const remaining = this._calculateRemaining(
        item.currentAmount,
        item.targetAmount
      );
      const symbol = item.currencySymbol || '$';

      return html`
        <div
          class="progress-bar-bg"
          role="progressbar"
          aria-valuenow=${percentage}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label=${item.title}
        >
          <div class="progress-bar-fill" style="width: ${percentage}%"></div>
        </div>
        <div class="status-text">
          ${percentage}% recaudado — faltan
          ${this._formatCurrency(remaining, symbol)}
        </div>
      `;
    };

    return html`
      <section class="transparency-container" aria-label="Transparencia Total">
        <div class="content-wrapper">
          ${this.sectionTag
            ? html`<div class="section-tag">${this.sectionTag}</div>`
            : ''}

          <h2 class="section-title">${this.title}</h2>

          <p class="description">${this.description}</p>

          <div class="featured-goal" id=${primaryGoal.id}>
            <div class="card-header">
              <h3 class="item-title">${primaryGoal.title}</h3>
              <span class="item-amount">
                ${primaryGoal.targetLabel ||
                this._formatCurrency(
                  primaryGoal.targetAmount,
                  primaryGoal.currencySymbol || '$'
                )}
              </span>
            </div>
            <p class="item-desc">${primaryGoal.description}</p>
            ${renderGoalProgress(primaryGoal)}
            <button
              class="primary-action"
              type="button"
              @click=${() =>
                this.dispatchEvent(
                  new CustomEvent('donate', {
                    bubbles: true,
                    composed: true,
                  })
                )}
            >
              Ayuda a completar la meta
            </button>
          </div>

          ${secondaryGoals.length > 0
            ? html`
                <div class="secondary-goals">
                  <h3 class="secondary-title">Próximas metas</h3>
                  <div class="secondary-list">
                    ${secondaryGoals.map((item) => {
                      const symbol = item.currencySymbol || '$';
                      return html`
                        <div class="secondary-goal" id=${item.id}>
                          <div>
                            <h4 class="item-title">${item.title}</h4>
                            <p class="item-desc">${item.description}</p>
                          </div>
                          <span class="item-amount">
                            ${item.targetLabel ||
                            this._formatCurrency(item.targetAmount, symbol)}
                          </span>
                        </div>
                      `;
                    })}
                  </div>
                </div>
              `
            : ''}
          </div>
        </div>
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rftw-transparency': RftwTransparency;
  }
}

